import { useMemo } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import { getPlural } from 'lib/string/pluralize'
import { capitalise } from 'lib/string/stringUtils'

interface FormatSaleUnitLabelOptions {
  withLeadingSlash?: boolean
}

interface FormatSaleUnitLabelFn {
  (saleUnit: string, options?: FormatSaleUnitLabelOptions): string
  (saleUnit?: string, options?: FormatSaleUnitLabelOptions): string | undefined
}

/**
 * Returns an ICU-pluralized "count + saleUnit" string (e.g. "2 rooms", "1 villa")
 * for all known sale unit values, with a getPlural fallback for unknown units.
 *
 * Use the hook version (`useSaleUnitFormatting`) in React components.
 * Use this standalone function in selectors/utils that already receive `intl`.
 */
export function formatSaleUnitCount(intl: IntlShape, saleUnit: string, count: number): string {
  const key = saleUnit.toLowerCase()
  const startsWithCapital = saleUnit.length > 0 && saleUnit[0] === saleUnit[0].toUpperCase() && saleUnit[0] !== saleUnit[0].toLowerCase()

  let result: string | undefined

  if (key === 'room') {
    result = intl.formatMessage({
      id: 'nLcmGau2j6XBvHXOKwC6',
      defaultMessage: '{count, plural, one {# room} other {# rooms}}',
    }, { count })
  } else if (key === 'night') {
    result = intl.formatMessage({
      id: 'h3_iVk6wVBAnOg1R2eny',
      defaultMessage: '{count, plural, one {# night} other {# nights}}',
    }, { count })
  } else if (key === 'villa') {
    result = intl.formatMessage({
      id: 'VWLEz2TZE_v27KGj7_p2',
      defaultMessage: '{count, plural, one {# villa} other {# villas}}',
    }, { count })
  } else if (key === 'suite') {
    result = intl.formatMessage({
      id: 'iFxQNxrb3i1z963Dp1XJ',
      defaultMessage: '{count, plural, one {# suite} other {# suites}}',
    }, { count })
  } else if (key === 'guest') {
    result = intl.formatMessage({
      id: 'WlE6FWgCVfrbvQPPIndu',
      defaultMessage: '{count, plural, one {# guest} other {# guests}}',
    }, { count })
  } else if (key === 'adult') {
    result = intl.formatMessage({
      id: '6SlT4c8Aa4PXEwaHG0N6',
      defaultMessage: '{count, plural, one {# adult} other {# adults}}',
    }, { count })
  } else if (key === 'traveller') {
    result = intl.formatMessage({
      id: '4gz2GNYpPhcRf2JMJtIZ',
      defaultMessage: '{count, plural, one {# traveller} other {# travellers}}',
    }, { count })
  } else if (key === 'person') {
    result = intl.formatMessage({
      id: '6-HH7f0_A5N4dtkBf-vX',
      defaultMessage: '{count, plural, one {# person} other {# people}}',
    }, { count })
  } else if (key === 'cabin') {
    result = intl.formatMessage({
      id: 'nwc7xwFhLB5fnrutCxLj',
      defaultMessage: '{count, plural, one {# cabin} other {# cabins}}',
    }, { count })
  } else if (key === 'package') {
    result = intl.formatMessage({
      id: 'TR2vkZRKyP6-FXpBsPEv',
      defaultMessage: '{count, plural, one {# package} other {# packages}}',
    }, { count })
  } else if (key === 'property') {
    result = intl.formatMessage({
      id: 'oD4BmJJv37z1mAnb_5df',
      defaultMessage: '{count, plural, one {# property} other {# properties}}',
    }, { count })
  }

  if (result) {
    if (startsWithCapital) {
      // Preserve capitalization of the unit word (e.g. "2 rooms" -> "2 Rooms")
      const spaceIdx = result.indexOf(' ')
      if (spaceIdx !== -1 && spaceIdx + 1 < result.length) {
        return result.slice(0, spaceIdx + 1) + result[spaceIdx + 1].toUpperCase() + result.slice(spaceIdx + 2)
      }
    }
    return result
  }

  // i18n: fallback for unknown sale units - uses getPlural (English-only)
  return count === 1 ?
      intl.formatMessage({
        id: 'UhfSXOtPHDYGUz7gvTVL',
        defaultMessage: '{count} {saleUnit}',
      }, { count, saleUnit }) :
      intl.formatMessage({
        id: 't4acHgq11prf8rMioDjd',
        defaultMessage: '{count} {saleUnitPlural}',
      }, { count, saleUnitPlural: getPlural(saleUnit) })
}

/**
 * Returns a localized plural noun for a known sale unit (e.g. "rooms", "villas", "people").
 * Falls back to getPlural for unknown units.
 *
 * Use the hook version (`useSaleUnitFormatting`) in React components.
 * Use this standalone function in selectors/utils that already receive `intl`.
 */
export function getSaleUnitPluralNoun(intl: IntlShape, saleUnit: string): string {
  const startsWithCapital = saleUnit.length > 0 && saleUnit[0] === saleUnit[0].toUpperCase() && saleUnit[0] !== saleUnit[0].toLowerCase()

  const known: Record<string, string> = {
    adult: intl.formatMessage({
      id: 'tq9Xh89UdyRc86xIHbsT',
      defaultMessage: 'adults',
    }),
    cabin: intl.formatMessage({
      id: 'snseQP9Ydp-vbAABoh-S',
      defaultMessage: 'cabins',
    }),
    day: intl.formatMessage({
      id: 'o1CwBf1SkKYLgU7g0LX7',
      defaultMessage: 'days',
    }),
    guest: intl.formatMessage({
      id: 'bxVtTbdimFHe_qt1Xzpd',
      defaultMessage: 'guests',
    }),
    night: intl.formatMessage({
      id: 'uZUM7IZFon9LJ0fqWGc9',
      defaultMessage: 'nights',
    }),
    package: intl.formatMessage({
      id: 'AaPkkqRkjUPbj1ioU4pd',
      defaultMessage: 'packages',
    }),
    person: intl.formatMessage({
      id: 'cgf7L7VO9jbta4G-F0Ag',
      defaultMessage: 'people',
    }),
    property: intl.formatMessage({
      id: 'P7A5c_CUF5_3EnXKHpqG',
      defaultMessage: 'properties',
    }),
    rental: intl.formatMessage({
      id: '_0JxTUgUo8FajZr47IVa',
      defaultMessage: 'rentals',
    }),
    room: intl.formatMessage({
      id: 'bM2VXSyEtBg54Ejh-Spt',
      defaultMessage: 'rooms',
    }),
    suite: intl.formatMessage({
      id: 'fkaNvVOO9ATPi7xxc5Wa',
      defaultMessage: 'suites',
    }),
    traveler: intl.formatMessage({
      id: 'XZZfQT03fv9oXzFKNh9Y',
      defaultMessage: 'travelers',
    }),
    traveller: intl.formatMessage({
      id: 'EzPLAfvOJ1s9hyj7r5H8',
      defaultMessage: 'travellers',
    }),
    villa: intl.formatMessage({
      id: 'lq1zb93v2YH5kl2-uBQf',
      defaultMessage: 'villas',
    }),
  }

  const result = known[saleUnit.toLowerCase()]

  if (result) {
    if (startsWithCapital) {
      return capitalise(result)
    }
    return result
  }

  // i18n: fallback for unknown sale units - uses getPlural (English-only)
  return getPlural(saleUnit)
}

/**
 * Returns a localized singular sale unit label (e.g. "/room", "total", "/person*").
 *
 * Use this when the UI needs the unit label by itself rather than a counted phrase.
 */
export function formatSaleUnitLabel(
  intl: IntlShape,
  saleUnit: string,
  options?: FormatSaleUnitLabelOptions,
): string
export function formatSaleUnitLabel(
  intl: IntlShape,
  saleUnit?: string,
  options?: FormatSaleUnitLabelOptions,
): string | undefined
export function formatSaleUnitLabel(
  intl: IntlShape,
  saleUnit?: string,
  options: FormatSaleUnitLabelOptions = {},
): string | undefined {
  if (!saleUnit) {
    return undefined
  }

  const hasLeadingSlash = saleUnit.startsWith('/')
  const hasAsterisk = saleUnit.endsWith('*')
  const strippedSaleUnit = saleUnit.replace(/^\//, '').replace(/\*$/, '')
  const normalizedSaleUnit = strippedSaleUnit.toLowerCase()
  const startsWithCapital = strippedSaleUnit.length > 0 &&
    strippedSaleUnit[0] === strippedSaleUnit[0].toUpperCase() &&
    strippedSaleUnit[0] !== strippedSaleUnit[0].toLowerCase()

  const localizedSaleUnit = (() => {
    switch (normalizedSaleUnit) {
      case 'adult':
        return intl.formatMessage({
          id: '-f206TRRuHyl-LIHwsRx',
          defaultMessage: 'adult',
        })
      case 'cabin':
        return intl.formatMessage({
          id: 'fnsTfgUBU1VIH5-qEIdh',
          defaultMessage: 'cabin',
        })
      case 'day':
        return intl.formatMessage({
          id: 'IFJ4D_yXEBRWRSrrQJHC',
          defaultMessage: 'day',
        })
      case 'dayPerPerson':
        return intl.formatMessage({
          id: '2T-RP8a7ccp11ORnBeNb',
          defaultMessage: 'day per person',
        })
      case 'guest':
        return intl.formatMessage({
          id: 'tard3zgFlUM1c8Npfmki',
          defaultMessage: 'guest',
        })
      case 'night':
        return intl.formatMessage({
          id: 'MURspPvTr5WlkK_VwwWP',
          defaultMessage: 'night',
        })
      case 'package':
        return intl.formatMessage({
          id: 'UAH6BoW9srE7wEKFxqcg',
          defaultMessage: 'package',
        })
      case 'person':
        return intl.formatMessage({
          id: 'a-c1CI8Lm4liycNIJk3a',
          defaultMessage: 'person',
        })
      case 'property':
        return intl.formatMessage({
          id: 'NbklkiFa1BwLLTtt7MGZ',
          defaultMessage: 'property',
        })
      case 'rental':
        return intl.formatMessage({
          id: 'MuvPvo_eOWU3WEihaHlX',
          defaultMessage: 'rental',
        })
      case 'room':
        return intl.formatMessage({
          id: 'FCCpbvntIABECfA7su6D',
          defaultMessage: 'room',
        })
      case 'suite':
        return intl.formatMessage({
          id: 'UeCuDfDGQlce_E5_xFvG',
          defaultMessage: 'suite',
        })
      case 'total':
        // Price book no longer shows a visible "total" sale-unit label (including "total*").
        // Consumers may still pass saleUnit="total"; other units (person, night, …) are unchanged.
        return null
      case 'traveler':
        return intl.formatMessage({
          id: 'XpTz0bInxd712UrMo0rQ',
          defaultMessage: 'traveler',
        })
      case 'traveller':
        return intl.formatMessage({
          id: 't__SzkPvir7BRW1EniEC',
          defaultMessage: 'traveller',
        })
      case 'villa':
        return intl.formatMessage({
          id: 'eGYNilWcgIbb7fsLoht6',
          defaultMessage: 'villa',
        })
      default:
        return strippedSaleUnit
    }
  })()

  if (localizedSaleUnit === null) {
    return ''
  }

  const formattedSaleUnit = startsWithCapital ? capitalise(localizedSaleUnit) : localizedSaleUnit
  const withLeadingSlash = options.withLeadingSlash ?? hasLeadingSlash
  const shouldPrefixWithSlash = withLeadingSlash && !/^(total|with)/.test(normalizedSaleUnit)

  if (shouldPrefixWithSlash && hasAsterisk) {
    return intl.formatMessage(
      {
        id: '3oJZ7LjD4YYy7YI6cJRT',
        defaultMessage: '/{saleUnit}*',
      },
      { saleUnit: formattedSaleUnit },
    )
  }

  if (shouldPrefixWithSlash) {
    return intl.formatMessage(
      {
        id: 'upBf5lM27l0oOdNxNEt5',
        defaultMessage: '/{saleUnit}',
      },
      { saleUnit: formattedSaleUnit },
    )
  }

  if (hasAsterisk) {
    return intl.formatMessage(
      {
        id: 'dboISEuDUI1RFhNEvZp_',
        defaultMessage: '{saleUnit}*',
      },
      { saleUnit: formattedSaleUnit },
    )
  }

  return formattedSaleUnit
}

interface SaleUnitFormattingFns {
  formatSaleUnitCount: (saleUnit: string, count: number) => string
  getSaleUnitPluralNoun: (saleUnit: string) => string
  formatSaleUnitLabel: FormatSaleUnitLabelFn
}

function useSaleUnitFormatting(): SaleUnitFormattingFns {
  const intl = useIntl()
  return useMemo(() => {
    function formatLocalizedSaleUnitLabel(
      saleUnit: string,
      options?: FormatSaleUnitLabelOptions,
    ): string
    function formatLocalizedSaleUnitLabel(
      saleUnit?: string,
      options?: FormatSaleUnitLabelOptions,
    ): string | undefined
    function formatLocalizedSaleUnitLabel(
      saleUnit?: string,
      options: FormatSaleUnitLabelOptions = {},
    ): string | undefined {
      return formatSaleUnitLabel(intl, saleUnit, options)
    }

    return {
      formatSaleUnitCount: (saleUnit: string, count: number) => formatSaleUnitCount(intl, saleUnit, count),
      getSaleUnitPluralNoun: (saleUnit: string) => getSaleUnitPluralNoun(intl, saleUnit),
      formatSaleUnitLabel: formatLocalizedSaleUnitLabel,
    }
  }, [intl])
}

export default useSaleUnitFormatting
